import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MuiLink from '@mui/material/Link';
import { Theme, useTheme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { isScheduleActive } from './myReportsListHelpers';
import { CELL_SX, ROW_ACTION_REVEAL_CLASS } from './myReportsRowStyles';
import { MyReport } from './MyReportsTable.types';
import { formatRecipients, formatScheduleSummary, parseScheduledDetails } from './scheduledDetails';
import { generateListPageReportURL } from '../../../utils/report';

type PillTone = 'default' | 'shared' | 'scheduled';

const pillPalette = (theme: Theme, tone: PillTone) => {
  switch (tone) {
    case 'default':
      return { bg: theme.palette.primary.light, fg: theme.palette.primary.dark };
    case 'shared':
      return { bg: theme.palette.info.light, fg: theme.palette.info.dark };
    case 'scheduled':
      return { bg: theme.palette.success.light, fg: theme.palette.success.dark };
  }
};

export const Pill: React.FC<{ label: string; tone: PillTone }> = ({ label, tone }) => {
  const theme = useTheme();
  const { bg, fg } = pillPalette(theme, tone);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        bgcolor: bg,
        color: fg,
        fontSize: '10px',
        fontWeight: 600,
        px: 1,
        py: '2px',
        borderRadius: 1,
      }}
    >
      {label}
    </Box>
  );
};

export const NameCell: React.FC<{
  row: MyReport;
  isMine: boolean;
  isClient: boolean;
  onSelect?: () => void;
}> = ({ row, isMine, isClient, onSelect }) => {
  const theme = useTheme();
  const nameSx = {
    color: theme.palette.v4.accent,
    fontWeight: 500,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      {onSelect ? (
        // Embedded in a host page (its Reports modal): applying the report is a
        // state change on the page behind, not a navigation, so a link with an
        // href would be a lie — and middle-click would open a dead route.
        <MuiLink
          component="button"
          type="button"
          onClick={onSelect}
          sx={{
            ...nameSx,
            background: 'none',
            border: 'none',
            p: 0,
            font: 'inherit',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          {row.name}
        </MuiLink>
      ) : (
        <MuiLink
          component={RouterLink}
          to={generateListPageReportURL(row.savedReportType?.name ?? '', row.code)}
          sx={nameSx}
        >
          {row.name}
        </MuiLink>
      )}
      {row.isDefault && isMine && <Pill label="Default" tone="default" />}
      {!isClient && row.sharedWithIds && <Pill label="Shared" tone="shared" />}
      {!isClient && isScheduleActive(row.scheduledDetails) && (
        <Pill label="Scheduled" tone="scheduled" />
      )}
    </Box>
  );
};

export const ScheduledCell: React.FC<{ raw: string | undefined }> = ({ raw }) => {
  if (!raw) return null;
  const parsed = parseScheduledDetails(raw);
  if (!parsed) return <>{raw}</>;
  const scheduleLine = formatScheduleSummary(parsed.schedule);
  const recipientsLine = formatRecipients(parsed.recipients);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', lineHeight: 1.3 }} title={raw}>
      {scheduleLine && <Box component="span">{scheduleLine}</Box>}
      {recipientsLine && (
        <Box component="span" sx={{ color: 'text.secondary', fontSize: '11px' }}>
          {recipientsLine}
        </Box>
      )}
    </Box>
  );
};

export const ActionCell: React.FC<{
  row: MyReport;
  canEdit: boolean;
  canDelete: boolean;
  isPinned?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onTogglePin?: () => void;
  onToggleDefault?: () => void;
  bg: string;
}> = ({
  row,
  canEdit,
  canDelete,
  isPinned,
  onEdit,
  onDelete,
  onTogglePin,
  onToggleDefault,
  bg,
}) => (
  <TableCell
    sx={{
      ...CELL_SX,
      position: 'sticky',
      right: 0,
      bgcolor: bg,
      zIndex: 1,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}>
      {onToggleDefault && (
        <Tooltip
          title={row.isDefault ? `Remove ${row.name} as default` : `Set ${row.name} as default`}
        >
          <IconButton
            size="small"
            aria-label={
              row.isDefault ? `Remove ${row.name} as default` : `Set ${row.name} as default`
            }
            aria-pressed={!!row.isDefault}
            onClick={onToggleDefault}
            // An active star stays visible unhovered — hiding it would take the
            // "this is your default" signal off the row entirely.
            className={row.isDefault ? undefined : ROW_ACTION_REVEAL_CLASS}
            sx={{
              p: 0.25,
              color: row.isDefault ? 'warning.main' : 'text.secondary',
              opacity: row.isDefault ? 1 : 0,
              transition: 'opacity 0.15s ease',
              '&:focus-visible': { opacity: 1 },
            }}
          >
            {row.isDefault ? (
              <StarIcon sx={{ fontSize: 16 }} />
            ) : (
              <StarOutlineIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      )}
      {onTogglePin && (
        <Tooltip title={isPinned ? `Unpin ${row.name} from its tabs` : `Pin ${row.name} as a tab`}>
          <IconButton
            size="small"
            aria-label={isPinned ? `Unpin ${row.name} from its tabs` : `Pin ${row.name} as a tab`}
            aria-pressed={!!isPinned}
            onClick={onTogglePin}
            // Likewise: a pinned report has no other indicator on the row, so
            // its pin must not vanish when the pointer leaves.
            className={isPinned ? undefined : ROW_ACTION_REVEAL_CLASS}
            sx={{
              p: 0.25,
              color: isPinned ? 'v4.accent' : 'text.secondary',
              opacity: isPinned ? 1 : 0,
              transition: 'opacity 0.15s ease',
              '&:focus-visible': { opacity: 1 },
            }}
          >
            {isPinned ? (
              <PushPinIcon sx={{ fontSize: 16 }} />
            ) : (
              <PushPinOutlinedIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      )}
      {canEdit && (
        <IconButton size="small" aria-label={`Edit ${row.name}`} onClick={onEdit}>
          <Edit sx={{ fontSize: 16 }} />
        </IconButton>
      )}
      {canDelete && (
        <IconButton size="small" aria-label={`Remove ${row.name}`} onClick={onDelete}>
          <Delete sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  </TableCell>
);
